// /register — 密碼帳號註冊（00 §2 / core §2）。
// POST /v3/auth/register 不發 token：成功後帳號 status=pending_verification，
// 後端寄 verify-email magic link。本頁僅顯示「請至信箱完成驗證」結果，不自動登入。

import { useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { ApiError, register } from '../../lib/api';
import { brand } from '../../lib/brand';
import { passwordMeetsPolicy, passwordPolicyError, passwordPolicyHint, policyFromApiError } from '../../lib/passwordPolicy';
import { PasswordInput } from '../../components/PasswordInput';

export default function RegisterPage() {
  const { t } = useTranslation();
  const { status } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  // 00 §2.2（APP#59）：政策動態化——bootstrap 有效政策起始，422 帶回的 server 政策覆寫刷新。
  const [policy, setPolicy] = useState(() => brand.passwordPolicy);

  if (status === 'authenticated') {
    return <Navigate to="/home" replace />;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const name = displayName.trim();
    if (name.length < 1 || name.length > 32) {
      setError(t('auth.register.nameInvalid'));
      return;
    }
    if (!passwordMeetsPolicy(password, policy)) {
      setError(passwordPolicyError(policy, t));
      return;
    }
    setBusy(true);
    try {
      await register({ email: email.trim(), password, display_name: name });
      setDone(true);
    } catch (err) {
      const serverPolicy = policyFromApiError(err);
      if (err instanceof ApiError && (err.status === 409 || err.code === 'EMAIL_TAKEN')) {
        setError(t('auth.register.emailTaken'));
      } else if (err instanceof ApiError && err.code === 'WEAK_PASSWORD') {
        // server=最終權威:帶 policy 時以 server 值刷新提示與後續前端驗證。
        if (serverPolicy) setPolicy(serverPolicy);
        setError(passwordPolicyError(serverPolicy ?? policy, t));
      } else if (err instanceof ApiError) {
        setError(t('auth.register.failGeneric', { msg: err.message }));
      } else {
        setError(t('auth.register.failNetwork'));
      }
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--spacing-8)' }}>
        <section className="card" style={{ width: 'min(400px, 100%)', textAlign: 'center' }}>
          <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-3xl)' }}>{t('auth.register.doneTitle')}</h1>
          <p className="text-secondary">{t('auth.register.doneBody', { email: email.trim() })}</p>
          <button className="btn-primary" onClick={() => navigate('/login', { replace: true })}>
            {t('auth.toLogin')}
          </button>
        </section>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '100vh', padding: 'var(--spacing-8)' }}>
      <section className="card" style={{ width: 'min(400px, 100%)' }}>
        <h1 style={{ marginTop: 0, fontSize: 'var(--font-size-4xl)', letterSpacing: 'var(--letter-spacing-tight)' }}>{t('auth.register.title')}</h1>
        <p className="text-secondary" style={{ marginTop: 'calc(-1 * var(--spacing-4))' }}>
          {t('auth.register.subtitle')}
        </p>
        <form onSubmit={onSubmit} style={{ display: 'grid', gap: 'var(--spacing-6)' }}>
          <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
              {t('auth.email')}
            </span>
            <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={error !== null} />
          </label>
          <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
              {t('auth.register.displayName')}
            </span>
            <input type="text" autoComplete="name" required maxLength={32} value={displayName} onChange={(e) => setDisplayName(e.target.value)} aria-invalid={error !== null} />
          </label>
          <label style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
            <span className="text-secondary" style={{ fontSize: 'var(--font-size-md)' }}>
              {t('auth.password')}
            </span>
            <PasswordInput autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={error !== null} aria-describedby="register-pw-hint" />
            <span id="register-pw-hint" className="text-secondary" style={{ fontSize: 'var(--font-size-sm)' }}>
              {passwordPolicyHint(policy, t)}
            </span>
          </label>
          {error && (
            <p className="error-text" role="alert" style={{ margin: 0 }}>
              {error}
            </p>
          )}
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? t('auth.register.submitBusy') : t('auth.register.submit')}
          </button>
        </form>
        <p className="text-secondary" style={{ marginBottom: 0, fontSize: 'var(--font-size-md)' }}>
          {t('auth.register.haveAccount')}<Link to="/login">{t('auth.toLogin')}</Link>
        </p>
      </section>
    </div>
  );
}
